import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy, ChangeDetectorRef, OnInit, ViewEncapsulation, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormControl } from '@angular/forms';
import { firstValueFrom } from 'rxjs';

import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';

import {
  JobCredentialRequirementsApiService,
  JobCredentialRequirement,
  CredentialTypeMetadata,
  SetRequirementPayload
} from 'src/shared/services/job-credential-requirements-api.service';

import { Util } from 'src/shared/util/util';

export interface RequirementRow {
  credential_type: string;
  label: string;
  strictness: string;
  control: FormControl;
}

@Component({
  selector: 'pro-requirements',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TidyUiComponentsModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  styles: [`
    pro-requirements .pro-requirements-buttons {
      display: flex;
      justify-content: flex-end;
      align-items: stretch;

      tidy-button {
        display: inline-flex;
        width: auto;
        margin-left: 8px;
      }
    }

    pro-requirements .requirement-badge {
      display: inline-flex;
      align-items: center;
      padding: 4px 10px;
      border-radius: 20px;
      font-size: 12px;
      font-weight: 500;
      gap: 4px;
      white-space: nowrap;
    }

    pro-requirements .requirement-mandatory {
      background-color: rgba(76, 175, 80, 0.15);
      color: #4caf50;

      .requirement-badge-icon img {
        filter: brightness(0) saturate(100%) invert(55%) sepia(69%) saturate(456%) hue-rotate(83deg) brightness(96%) contrast(89%);
      }
    }

    pro-requirements .requirement-preferred {
      background-color: rgba(255, 193, 7, 0.15);
      color: #ffc107;

      .requirement-badge-icon img {
        filter: brightness(0) saturate(100%) invert(78%) sepia(62%) saturate(1000%) hue-rotate(358deg) brightness(103%) contrast(106%);
      }
    }

    pro-requirements .requirement-badge-icon {
      width: 14px;
      height: 14px;
    }

    pro-requirements .requirement-badge-text {
      font-weight: 500;
    }

    pro-requirements .requirement-badge-selectable {
      cursor: pointer;
      transition: opacity 0.15s ease;
      user-select: none;

      &:hover {
        transform: scale(1.05);
      }

      &:active {
        transform: scale(0.97);
      }
    }

    pro-requirements .requirement-badge-inactive {
      background-color: rgba(255, 255, 255, 0.06);
      color: rgba(255, 255, 255, 0.4);
      opacity: 0.6;

      .requirement-badge-icon img {
        filter: brightness(0) invert(1) !important;
        opacity: 0.4;
      }

      &:hover {
        opacity: 1;
      }
    }

    pro-requirements .white-icon img {
      filter: brightness(0) invert(1);
    }

    pro-requirements .strictness-badges {
      display: flex;
      gap: 8px;
      cursor: pointer;
    }
  `],
  template: `
    <tidy-card>
      <tidy-row [alignLastItemRight]="!editing && requirements.length > 0" class="vertical-align-center extra-bottom-padding">
        <tidy-row class="vertical-align-center">
          <tidy-image
            [src]="'assets/img/shield-green.svg'"
            class="title-size" />
          <tidy-text
            [header]="'Pro Requirements'" />
        </tidy-row>
        @if (loaded && !editing && requirements.length > 0) {
          <tidy-image
            [src]="'assets/svg/create-outline.svg'"
            class="body-size section-remove-icon"
            (action)="toggleEdit()" />
        }
      </tidy-row>
      <tidy-row>
        <tidy-text
          [helper]="'Set credential requirements for your Pros.'" />
      </tidy-row>

      @if (scopeType === 'Address' && !hasCustomRequirements && loaded) {
        <tidy-alert class="light-green-background">
          <tidy-row [alignLastItemRight]="true" class="vertical-align-center">
            <tidy-row class="vertical-align-center">
              <tidy-image
                [src]="'assets/svg/information-circle.svg'"
                class="body-size white-icon" />
              <tidy-text
                [body]="'Using your default requirements.'" />
            </tidy-row>
            <tidy-text
              class="link"
              [actionBody]="'View Account Settings'"
              (action)="goToAccountSettings.emit()" />
          </tidy-row>
        </tidy-alert>
      }

      @if (scopeType === 'Address' && hasCustomRequirements && loaded) {
        <tidy-alert class="yellow-background">
          <tidy-row class="vertical-align-center">
            <tidy-image
              [src]="'assets/svg/information-circle.svg'"
              class="body-size white-icon" />
            <tidy-text
              [body]="'These override your default requirements.'" />
          </tidy-row>
        </tidy-alert>
      }

      @if (loaded && (scopeType === 'Customer' || hasCustomRequirements)) {
        @if (!editing && requirements.length > 0) {
          @for (req of visibleRequirements; track req.credential_type) {
            <tidy-row class="extra-top-padding">
              <tidy-divider />
            </tidy-row>
            <tidy-row [alignLastItemRight]="true" class="vertical-align-center extra-top-padding">
              <tidy-row class="vertical-align-center">
                <tidy-image
                  [src]="'assets/img/checkmark-circle-green.svg'"
                  class="body-size" />
                <tidy-text
                  [body]="req.label" />
              </tidy-row>
              <span class="requirement-badge" [class.requirement-mandatory]="req.strictness === 'mandatory'" [class.requirement-preferred]="req.strictness !== 'mandatory'">
                <tidy-image
                  [src]="req.strictness === 'mandatory' ? 'assets/svg/shield-checkmark.svg' : 'assets/svg/star.svg'"
                  class="requirement-badge-icon" />
                <span class="requirement-badge-text">{{ req.strictness === 'mandatory' ? 'Required' : 'Preferred' }}</span>
              </span>
            </tidy-row>
          }
          @if (requirements.length > 5) {
            <tidy-row [align]="'center'" class="extra-top-padding">
              <tidy-text
                class="link"
                [actionBody]="showAllRequirements ? 'Show Less' : 'Show More (' + (requirements.length - 5) + ')'"
                (action)="toggleShowAll()" />
            </tidy-row>
          }
        }

        @if (editing || requirements.length === 0) {
          @for (req of requirements; track req.credential_type) {
            <tidy-row class="extra-top-padding">
              <tidy-divider />
            </tidy-row>
            <tidy-row [alignLastItemRight]="true" class="vertical-align-center extra-top-padding">
              <tidy-row class="vertical-align-center">
                <tidy-image
                  [src]="'assets/img/checkmark-circle-green.svg'"
                  class="body-size" />
                <tidy-text
                  [body]="req.label" />
              </tidy-row>
              <tidy-image
                [src]="'assets/svg/trash-outline.svg'"
                class="body-size section-remove-icon"
                (action)="removeRequirement(req.credential_type)" />
            </tidy-row>
            <div class="strictness-badges extra-top-padding">
              <span
                class="requirement-badge requirement-badge-selectable"
                [class.requirement-mandatory]="req.strictness === 'mandatory'"
                [class.requirement-badge-inactive]="req.strictness !== 'mandatory'"
                (click)="onStrictnessChange(req, 'mandatory')">
                @if (req.strictness === 'mandatory') {
                  <tidy-image
                    [src]="'assets/svg/checkmark.svg'"
                    class="requirement-badge-icon" />
                }
                <tidy-image
                  [src]="'assets/svg/shield-checkmark.svg'"
                  class="requirement-badge-icon" />
                <span class="requirement-badge-text">Required</span>
              </span>
              <span
                class="requirement-badge requirement-badge-selectable"
                [class.requirement-preferred]="req.strictness === 'preferred'"
                [class.requirement-badge-inactive]="req.strictness !== 'preferred'"
                (click)="onStrictnessChange(req, 'preferred')">
                @if (req.strictness === 'preferred') {
                  <tidy-image
                    [src]="'assets/svg/checkmark.svg'"
                    class="requirement-badge-icon" />
                }
                <tidy-image
                  [src]="'assets/svg/star.svg'"
                  class="requirement-badge-icon" />
                <span class="requirement-badge-text">Preferred</span>
              </span>
            </div>
          }

          @if (availableTypeItems.length > 0) {
            <tidy-row class="extra-top-padding">
              <tidy-divider />
            </tidy-row>
            <tidy-row class="extra-top-padding">
              <tidy-select
                [label]="'Add Requirement'"
                [items]="availableTypeItems"
                [form]="addControl"
                [formControl]="addControl"
                (optionChange)="addRequirement($event)" />
            </tidy-row>
          } @else if (requirements.length === 0) {
            <tidy-row class="extra-bottom-padding extra-top-padding">
              <tidy-text
                [helper]="'No requirements set.'" />
            </tidy-row>
            @if (allCredentialTypes.length === 0) {
              <tidy-row class="extra-bottom-padding">
                <tidy-text
                  [helper]="'No credential types available yet. Credential types will appear here once configured.'" />
              </tidy-row>
            }
          }

          <div class="pro-requirements-buttons extra-top-padding">
            @if (editing) {
              <tidy-button
                [text]="'Cancel'"
                class="secondary"
                [smallText]="true"
                [action]="boundCancel" />
            }
            @if (editing || requirements.length > 0) {
              <tidy-button
                [text]="'Save'"
                class="primary"
                [smallText]="true"
                [action]="boundSave" />
            }
          </div>
        }
      }

      @if (scopeType === 'Address' && hasCustomRequirements && loaded) {
        <tidy-row [align]="'center'" class="extra-top-padding">
          <tidy-text
            class="link red"
            [actionBody]="'Reset to Defaults'"
            (action)="resetToDefaults()" />
        </tidy-row>
      }

      @if (scopeType === 'Address' && !hasCustomRequirements && loaded) {
        <tidy-button
          [text]="'Set Custom Requirements'"
          class="secondary"
          [action]="boundEnableCustom" />
      }

      @if (errorMessage) {
        <tidy-error-message
          [text]="errorMessage" />
      }
    </tidy-card>
  `
})
export class ProRequirementsComponent implements OnInit {
  @Input() scopeType: 'Customer' | 'Address' = 'Customer';
  @Input() scopeId: number;
  @Output() goToAccountSettings = new EventEmitter<void>();

  private readonly api = inject(JobCredentialRequirementsApiService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly util = inject(Util);

  requirements: RequirementRow[] = [];
  allCredentialTypes: CredentialTypeMetadata[] = [];
  availableTypes: CredentialTypeMetadata[] = [];
  availableTypeItems: { viewValue: string; value: string }[] = [];
  hasCustomRequirements = false;
  loaded = false;
  editing = false;
  showAllRequirements = false;
  errorMessage: string;

  strictnessItems = [
    { viewValue: 'Required', value: 'mandatory', icon: 'assets/svg/shield-checkmark.svg' },
    { viewValue: 'Preferred', value: 'preferred', icon: 'assets/svg/star.svg' }
  ];

  addControl = new FormControl('');

  boundSave = this.save.bind(this);
  boundCancel = this.cancelEdit.bind(this);
  boundEnableCustom = this.enableCustomRequirements.bind(this);

  get scope(): string {
    return this.scopeType === 'Customer' ? 'customer' : 'address';
  }

  async ngOnInit(): Promise<void> {
    try {
      const [types, requirements] = await Promise.all([
        firstValueFrom(this.api.getCredentialTypes()),
        firstValueFrom(this.api.getRequirements(this.scope, this.scopeId))
      ]);
      this.allCredentialTypes = types;
      this.hasCustomRequirements = this.scopeType === 'Customer' || requirements.length > 0;
      this.buildRequirementRows(requirements);
      this.updateAvailableTypes();
      this.loaded = true;
      this.cdr.markForCheck();
    } catch (err: any) {
      this.errorMessage = err?.error?.message || err?.message || 'Failed to load credential types. Please try again.';
      this.loaded = true;
      this.cdr.markForCheck();
    }
  }

  get visibleRequirements(): RequirementRow[] {
    if (this.showAllRequirements) {
      return this.requirements;
    }
    return this.requirements.slice(0, 5);
  }

  toggleShowAll(): void {
    this.showAllRequirements = !this.showAllRequirements;
    this.cdr.markForCheck();
  }

  toggleEdit(): void {
    this.editing = true;
    this.cdr.markForCheck();
  }

  async cancelEdit(): Promise<void> {
    try {
      const requirements = await firstValueFrom(
        this.api.getRequirements(this.scope, this.scopeId)
      );
      this.buildRequirementRows(requirements);
      this.updateAvailableTypes();
      this.editing = false;
      this.cdr.markForCheck();
    } catch {
      this.editing = false;
      this.cdr.markForCheck();
    }
  }

  onStrictnessChange(req: RequirementRow, value: string): void {
    req.strictness = value;
    req.control.setValue(value);
    this.cdr.markForCheck();
  }

  addRequirement(credentialType: string): void {
    if (!credentialType) {return;}
    const meta = this.allCredentialTypes.find(t => t.key === credentialType);
    if (!meta) {return;}

    this.editing = true;
    this.requirements = [
      ...this.requirements,
      {
        credential_type: credentialType,
        label: meta.key_label,
        strictness: 'mandatory',
        control: new FormControl('mandatory')
      }
    ];
    this.updateAvailableTypes();
    this.addControl.setValue('');
    this.cdr.markForCheck();
  }

  removeRequirement(credentialType: string): void {
    this.requirements = this.requirements.filter(r => r.credential_type !== credentialType);
    this.updateAvailableTypes();
    this.cdr.markForCheck();
  }

  async save(): Promise<void> {
    try {
      const payload: SetRequirementPayload[] = this.requirements.map(r => ({
        credential_type_key: r.credential_type,
        strictness: r.strictness
      }));
      const result = await firstValueFrom(
        this.api.setRequirements(this.scope, payload, this.scopeId)
      );
      this.hasCustomRequirements = true;
      this.editing = false;
      this.buildRequirementRows(result);
      this.updateAvailableTypes();
      this.util.showSuccess('Requirements saved successfully.');
      this.cdr.markForCheck();
    } catch (err: any) {
      this.errorMessage = err?.error?.message || err?.message || 'Failed to save requirements';
      this.cdr.markForCheck();
    }
  }

  async resetToDefaults(): Promise<void> {
    try {
      await firstValueFrom(
        this.api.setRequirements(this.scope, [], this.scopeId)
      );
      this.requirements = [];
      this.hasCustomRequirements = false;
      this.updateAvailableTypes();
      this.util.showSuccess('Reset to default requirements.');
      this.cdr.markForCheck();
    } catch (err: any) {
      this.errorMessage = err?.error?.message || err?.message || 'Failed to reset requirements';
      this.cdr.markForCheck();
    }
  }

  async enableCustomRequirements(): Promise<void> {
    this.hasCustomRequirements = true;
    this.cdr.markForCheck();
  }

  private buildRequirementRows(requirements: JobCredentialRequirement[]): void {
    this.requirements = requirements.map(r => {
      const meta = this.allCredentialTypes.find(t => t.key === r.credential_type.key);
      return {
        credential_type: r.credential_type.key,
        label: meta?.key_label || r.credential_type.key_label || r.credential_type.key,
        strictness: r.strictness,
        control: new FormControl(r.strictness)
      };
    });
  }

  private updateAvailableTypes(): void {
    const usedKeys = new Set(this.requirements.map(r => r.credential_type));
    this.availableTypes = this.allCredentialTypes.filter(t => !usedKeys.has(t.key));

    const grouped = new Map<string, CredentialTypeMetadata[]>();
    for (const type of this.availableTypes) {
      const category = type.category_label || 'Other';
      if (!grouped.has(category)) {
        grouped.set(category, []);
      }
      grouped.get(category).push(type);
    }

    this.availableTypeItems = [];
    for (const [category, types] of grouped) {
      for (const type of types) {
        this.availableTypeItems.push({
          viewValue: `${type.key_label} (${category})`,
          value: type.key
        });
      }
    }
  }
}
